import React from "react";
import { CardBody, Table, Label, Input } from "reactstrap";

import MyPagination from "./pagination";
import { useCurrency } from "../_helper/currencyContext";
import { useLab } from "../_helper/PharmacyProvider";
import { Tooltip } from "react-tooltip";
import { FormLabel } from "react-bootstrap";
import { useTranslation } from "react-i18next";

export default function CommonTable({
    columns = [],
    data = [],
    onToggleStatus,
    onAction = [],
    totalPages,
    currentPage,
    setCurrentPage,
    entriesPerPage,
    setEntriesPerPage,
    minWidth
}) {
    const { getCurrencySymbol } = useCurrency();
    const { currentLab } = useLab();
    const { t } = useTranslation();

    const currencySymbol = getCurrencySymbol(currentLab?.baseCurrency);

    const defaultColWidth = columns.length <= 2 ? 240 : 160;

    const calculatedMinWidth = 50 +
        columns.reduce((acc, col) => {
            if (col.width && typeof col.width === "string" && col.width.endsWith("px")) {
                return acc + parseInt(col.width, 10);
            }
            return acc + defaultColWidth;
        }, 0) +
        (onToggleStatus ? 80 : 0) +
        (onAction?.length > 0 ? 150 : 0);

    const resolvedMinWidth = minWidth || `${Math.max(calculatedMinWidth, 750)}px`;

    const handlePageSizeChange = (e) => {
        const newSize = Number(e.target.value);
        setEntriesPerPage(newSize);
        setCurrentPage(1);
    };

    const getStartingSerialNumber = () => {
        return (currentPage - 1) * entriesPerPage + 1;
    };

    // Helper to safely display value or fallback to "-"
    const displayValue = (value) => {
        if (value === null || value === undefined || value === "") {
            return "-";
        }
        return value;
    };

    return (
        <CardBody className="tw-px-0">
            <div
                className="recent-table table-responsive currency-table tw-rounded-lg tw-border tw-border-black custom-slim-scrollbar"
                style={{ overflowX: "auto" }}
            >
                <Table
                    className="mb-0 table-center table-bordered table-hover doctor-table"
                    style={{ tableLayout: "fixed", minWidth: resolvedMinWidth, overflowX: "auto" }}
                >
                    <thead>
                        <tr>
                            <th className="text-center bg-primary" style={{ width: "50px" }}>
                                <b>#</b>
                            </th>
                            {columns.map((col, index) => (
                                <th
                                    key={index}
                                    style={{
                                        width: col.width || `${defaultColWidth}px`,
                                        whiteSpace: "nowrap"
                                    }}
                                    className="bg-primary"
                                >
                                    <b>{col.header}</b>
                                </th>
                            ))}
                            {onToggleStatus && (
                                <th className="text-center bg-primary" style={{ whiteSpace: "nowrap", width: '80px' }}>
                                    <b>{t("Status")}</b>
                                </th>
                            )}
                            {onAction.length > 0 && (
                                <th className="text-center bg-primary" style={{ whiteSpace: "nowrap", width: '150px' }}>
                                    <b>{t("Action")}</b>
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {data.length > 0 ? (
                            data.map((row, rowIndex) => {
                                const serialNumber = getStartingSerialNumber() + rowIndex;
                                return (
                                    <tr key={rowIndex}>
                                        <td className="text-center font-weight-bold">
                                            {serialNumber}
                                        </td>
                                        {columns.map((col, colIndex) => {
                                            const rawValue = row[col.accessor];
                                            const isCostColumn = col.header.toLowerCase().includes("cost") ||
                                                col.accessor.toLowerCase().includes("cost") ||
                                                col.accessor.toLowerCase().includes("price");

                                            let displayContent;

                                            if (col.render) {
                                                // If custom render is provided, use it (it should handle empty values itself)
                                                displayContent = col.render(rawValue, row);
                                            } else if (isCostColumn) {
                                                // Format price, but show "-" if no valid number
                                                const numValue = Number(rawValue);
                                                displayContent = !isNaN(numValue) && rawValue != null
                                                    ? <strong>
                                                        {currencySymbol}{' '}
                                                        {numValue.toLocaleString(undefined, {
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2,
                                                        })}
                                                    </strong>
                                                    : "-";
                                            } else {
                                                // Default: show value or "-"
                                                displayContent = displayValue(rawValue);
                                            }

                                            return (
                                                <td
                                                    key={colIndex}
                                                    style={col.style}
                                                >
                                                    {displayContent}
                                                </td>
                                            );
                                        })}

                                        {/* Status Toggle */}
                                        {onToggleStatus && (
                                            <td className="text-center">
                                                <Label className="switch doctor-switch">
                                                    <Input
                                                        type="checkbox"
                                                        checked={row.isActive === true}
                                                        onChange={() => onToggleStatus(row)}
                                                    />
                                                    <span className="switch-state"></span>
                                                </Label>
                                            </td>
                                        )}

                                        {/* Actions */}
                                        {onAction?.length > 0 && (
                                            <td className="text-center">
                                                <div className="d-inline-flex align-items-center justify-content-center">
                                                    {onAction.map((action, index) => {
                                                        if (typeof action.show === "function" && !action.show(row)) {
                                                            return null;
                                                        }
                                                        return (
                                                            <div
                                                                key={index}
                                                                onClick={() => action.onClick(row)}
                                                                className="tw-cursor-pointer tw-hover:bg-gray-100 tw-rounded tw-transition-colors"
                                                                title={action.label}
                                                            >
                                                                {action.label === "View" && (
                                                                    <>
                                                                        <Tooltip id="view" place="top" content={t("View")} />
                                                                        <button className='success-action-button me-1' data-tooltip-id="view">
                                                                            <i className="fa fa-eye" style={{ fontSize: "18px" }}></i>
                                                                        </button>
                                                                    </>
                                                                )}
                                                                {action.label === "Edit" && (
                                                                    <>
                                                                        <Tooltip id="edit" place="top" content={t("Edit")} />
                                                                        <button className='success-action-button me-1' data-tooltip-id="edit">
                                                                            <i className="fa fa-pencil-square" style={{ fontSize: "18px" }}></i>
                                                                        </button>
                                                                    </>
                                                                )}
                                                                {action.label === "Delete" && (
                                                                    <>
                                                                        <Tooltip id="delete" place="top" content={t("Delete")} />
                                                                        <button className='danger-action-button me-1' data-tooltip-id="delete">
                                                                            <i className="fa fa-trash tw-text-red-600" style={{ fontSize: "18px" }}></i>
                                                                        </button>
                                                                    </>
                                                                )}
                                                                {action.label === "Barcode" && (
                                                                    <>
                                                                        <Tooltip id="barcode" place="top" content={t("Preview Barcodes")} />
                                                                        <button className='success-action-button me-1' data-tooltip-id="barcode">
                                                                            <i className="fa fa-barcode" style={{ fontSize: "18px" }}></i>
                                                                        </button>
                                                                    </>
                                                                )}
                                                                {action.label === "Collect" && (
                                                                    <>
                                                                        <Tooltip id={`collect-${row._id || rowIndex}`} place="top" content={t("Collect Sample")} />
                                                                        <button className='success-action-button me-1' data-tooltip-id={`collect-${row._id || rowIndex}`}>
                                                                            <i className="bi bi-droplet-half" style={{ fontSize: "18px" }}></i>
                                                                        </button>
                                                                    </>
                                                                )}
                                                                {action.label === "Recollect" && (
                                                                    <>
                                                                        <Tooltip id={`recollect-${row._id || rowIndex}`} place="top" content={t("Recollect Sample")} />
                                                                        <button className='danger-action-button me-1' data-tooltip-id={`recollect-${row._id || rowIndex}`}>
                                                                            <i className="fa fa-refresh" style={{ fontSize: "18px" }}></i>
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td
                                    colSpan={
                                        columns.length +
                                        (onToggleStatus ? 1 : 0) +
                                        (onAction.length > 0 ? 1 : 0) +
                                        1 // +1 for serial number column
                                    }
                                    className="text-center py-4 text-muted"
                                >
                                    {t("No data available")}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="d-flex flex-wrap align-items-center justify-content-center justify-content-sm-between gap-2 mt-2 pt-1">
                    <div className="show-entries d-inline-flex align-items-center">
                        <FormLabel className="mb-0 text-muted" style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
                            {t("Show")}&nbsp;
                        </FormLabel>
                        <select
                            style={{
                                display: "inline-block",
                                width: "auto",
                                height: "28px",
                                padding: "0.15rem 1.6rem 0.15rem 0.5rem",
                                fontSize: "12px",
                                lineHeight: 1.2,
                                margin: "0 4px"
                            }}
                            value={entriesPerPage}
                            onChange={handlePageSizeChange}
                            className="form-control form-control-sm"
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={30}>30</option>
                            <option value={50}>50</option>
                        </select>
                        <FormLabel className="mb-0 text-muted" style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
                            {t("entries")}
                        </FormLabel>
                    </div>
                    <div className="d-flex justify-content-center justify-content-sm-end overflow-auto" style={{ maxWidth: "100%" }}>
                        <MyPagination totalPages={totalPages} currentPage={currentPage} onPageChange={setCurrentPage} />
                    </div>
                </div>
            )}
        </CardBody>
    );
}